import { Temporal } from '@js-temporal/polyfill';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  changeRulesQuery,
  CREW_CLASS,
  grantCrewSignIn,
  saveChangeRules,
  type ChangeRules,
  createCrewMember,
  crewPhoneSharingQuery,
  crewQuery,
  setCrewPhoneSharing,
  retireCrewMember,
  updateCrewMember,
  type CrewClass,
  type CrewMember,
  type CrewMemberInput,
} from '../api/crew.ts';
import { meQuery } from '../api/me.ts';
import { CrewCompliance, EnforcementSetting, RolesTickets } from './crew-compliance.tsx';
import { listQuery } from '../api/abilities.ts';
import { AbilityLists, abilitiesLine, CanDo } from './crew-abilities.tsx';
import { CrewTimeOff, TimeOffSettings } from './crew-time-off.tsx';
import { Button } from '../components/button.tsx';
import { AddButton, EditButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { TextArea } from '../components/text-area.tsx';

// CB-REQ CRW-01 at v1.960: everyone who works shifts — freelancers and staff alike — so a clash sees
// every rostered hour. A name, how to reach them, and a class; a subcontractor names their company.
// Rates, skills and availability come later in the crew phase.

export function CrewScreen() {
  const [showRetired, setShowRetired] = useState(false);
  const crew = useQuery(crewQuery(showRetired));
  const me = useQuery(meQuery);
  const mayManage = me.data?.capabilities.includes('crew.manage') ?? false;
  const mayApprove = me.data?.capabilities.includes('leave.approve') ?? false;
  const today = me.data ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toString() : '';
  const [adding, setAdding] = useState(false);
  // CMP-03 / CMP-05 at v1.965: one member's records open at a time, below the table, at full width
  // — inside a table that scrolls sideways on a phone they would be cut off.
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = crew.data?.find((c) => c.id === openId);
  const live = crew.data?.filter((c) => !c.retired).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Crew"
        title="Crew"
        meta={crew.data ? `${live} on the list` : undefined}
        actions={
          mayManage && !adding ? <AddButton noun="Crew" onClick={() => setAdding(true)} /> : null
        }
      />
      {adding ? <CrewForm onDone={() => setAdding(false)} /> : null}
      {crew.isPending ? <p className="text-muted">Loading…</p> : null}
      {crew.isError ? (
        <p role="alert" className="alert">
          {crew.error.message}
        </p>
      ) : null}
      {crew.data?.length === 0 && !adding ? (
        <p className="text-ink-soft">
          Nobody yet. Add the freelancers you call, and yourself and anyone else on staff who works
          shows, so a clash sees everyone’s hours.
        </p>
      ) : null}
      {crew.data && crew.data.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="data-table">
            <caption className="sr-only">Crew, by name</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Phone</th>
                <th scope="col">Email</th>
                <th scope="col">Class</th>
                {mayManage ? (
                  <th scope="col" className="sr-only">
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {crew.data.map((c) => (
                <CrewRow
                  key={c.id}
                  member={c}
                  mayManage={mayManage}
                  open={openId === c.id}
                  onOpen={() => setOpenId((id) => (id === c.id ? null : c.id))}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {opened ? (
        <section className="flex flex-col gap-2" aria-label={`${opened.displayName}’s records`}>
          <h2 className="section-title">{opened.displayName} — tickets and inductions</h2>
          <CrewCompliance member={opened} mayManage={mayManage && !opened.retired} today={today} />
          {/* CRW-03 at v1.9107: what they can do, which the eligible-crew filter reads. */}
          <h2 className="section-title">{opened.displayName} — can do</h2>
          <CanDo key={opened.id} member={opened} mayManage={mayManage && !opened.retired} />
          {/* CRW-02 at v1.9106: their unavailability and leave, and whether they enter their own. */}
          <h2 className="section-title">{opened.displayName} — unavailability and leave</h2>
          <CrewTimeOff
            member={opened}
            mayManage={mayManage && !opened.retired}
            mayApprove={mayApprove}
            today={today}
          />
        </section>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={showRetired}
          onChange={(e) => setShowRetired(e.currentTarget.checked)}
        />
        Show archived/retired
      </label>
      {/* CMP-07 at v1.965: each role's tickets, and where a gap is refused. */}
      <RolesTickets editable={mayManage} />
      <EnforcementSetting editable={mayManage} />
      {/* CRW-14 at v1.964: what a change to an accepted shift asks of the person. */}
      <ChangeRulesPanel mayManage={mayManage} />
      {/* CRW-02 at v1.9106: who enters their own on the phone, by class; the leave types. */}
      <TimeOffSettings editable={mayManage} />
      {/* CRW-03 at v1.9107: the tenant's skills and employment types. */}
      <AbilityLists editable={mayManage} />
    </div>
  );
}

function CrewRow({
  member,
  mayManage,
  open,
  onOpen,
}: {
  member: CrewMember;
  mayManage: boolean;
  /** Whether this member's tickets and inductions are open below the table. */
  open: boolean;
  onOpen: () => void;
}) {
  const queryClient = useQueryClient();
  // CRW-03 at v1.9107: tier and employment type beside class.
  const types = useQuery(listQuery('employment-types'));
  const abilities = abilitiesLine(member, types.data);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireCrewMember(member.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crew'] }),
  });
  const [offeringSignIn, setOfferingSignIn] = useState(false);
  const signIn = useMutation({
    mutationFn: () => grantCrewSignIn(member.id),
    onSuccess: async () => {
      setOfferingSignIn(false);
      await queryClient.invalidateQueries({ queryKey: ['crew'] });
    },
  });

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <CrewForm member={member} onDone={() => setEditing(false)} />
        </td>
      </tr>
    );
  }
  return (
    <tr className={member.retired ? 'text-muted' : undefined}>
      <th scope="row" className="font-semibold">
        {member.displayName}
        {member.retired ? <span className="tag ml-2">Retired</span> : null}
        {member.notes ? (
          <span className="mt-1 block text-sm font-normal text-ink-soft">{member.notes}</span>
        ) : null}
      </th>
      <td className="text-sm">
        {member.phone ? (
          <a className="link" href={`tel:${member.phone.replace(/\s/g, '')}`}>
            {member.phone}
          </a>
        ) : (
          '—'
        )}
        {member.phone && !member.retired ? (
          <PhoneSharing member={member} mayManage={mayManage} />
        ) : null}
      </td>
      <td className="text-sm">
        {member.email ?? '—'}
        <button
          type="button"
          className="link mt-1 block text-left"
          aria-expanded={open}
          onClick={onOpen}
        >
          Tickets <span aria-hidden="true">{open ? '▴' : '▾'}</span>
          <span className="sr-only"> for {member.displayName}</span>
        </button>
        {member.userId ? <span className="tag tag-accent mt-1 block w-fit">Signs in</span> : null}
      </td>
      <td>
        <span className="tag">{CREW_CLASS[member.class]}</span>
        {abilities ? <span className="mt-1 block text-sm text-ink-soft">{abilities}</span> : null}
        {member.companyName ? (
          <span className="mt-1 block text-sm text-ink-soft">{member.companyName}</span>
        ) : null}
      </td>
      {mayManage ? (
        <td className="text-right">
          {member.retired ? null : (
            <div className="flex flex-wrap justify-end gap-2">
              <EditButton what={member.displayName} onClick={() => setEditing(true)} />
              {!member.userId && member.email ? (
                <Button variant="quiet" onClick={() => setOfferingSignIn(true)}>
                  Provide app access…<span className="sr-only"> ({member.displayName})</span>
                </Button>
              ) : null}
              {confirming ? (
                <Button variant="quiet" disabled={retire.isPending} onClick={() => retire.mutate()}>
                  Yes, retire {member.displayName}
                </Button>
              ) : (
                <Button variant="quiet" onClick={() => setConfirming(true)}>
                  Retire<span className="sr-only"> {member.displayName}</span>
                </Button>
              )}
            </div>
          )}
          {offeringSignIn ? (
            <div className="mt-2 flex flex-col gap-2 text-left text-sm">
              <p>
                {member.displayName} will be able to see and answer their own shifts, signing in
                with a code emailed to {member.email}. AVenue sends nothing now — tell them the
                address of this site.
              </p>
              <div className="flex gap-2">
                <Button disabled={signIn.isPending} onClick={() => signIn.mutate()}>
                  Provide
                </Button>
                <Button variant="quiet" onClick={() => setOfferingSignIn(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
          {retire.isError || signIn.isError ? (
            <p role="alert" className="alert mt-2 text-left text-sm">
              {(retire.error ?? signIn.error)?.message}
            </p>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

/**
 * CRW-08 and CLI-07 at v1.979: whether their number is shown to the other crew on their jobs. They
 * set it themselves on My shifts; the office may record it for them, saying how they agreed.
 */
function PhoneSharing({ member, mayManage }: { member: CrewMember; mayManage: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const sharing = useQuery({ ...crewPhoneSharingQuery(member.id), enabled: open });
  const save = useMutation({
    mutationFn: (shared: boolean) => setCrewPhoneSharing(member.id, shared, note.trim()),
    onSuccess: (data) => {
      queryClient.setQueryData(crewPhoneSharingQuery(member.id).queryKey, data);
      setNote('');
    },
  });
  if (!open) {
    return (
      <button type="button" className="link mt-1 block text-left" onClick={() => setOpen(true)}>
        Contact visible?<span className="sr-only"> ({member.displayName})</span>
      </button>
    );
  }
  const p = sharing.data;
  return (
    <div className="mt-1 flex flex-col gap-2 text-left">
      {p ? (
        <p>
          {p.shared ? 'Shown to crew on their jobs.' : 'Not shown to crew.'}
          {p.recordedAt ? (
            <span className="block text-ink-soft">
              {p.onBehalf ? `Recorded by ${p.recordedBy ?? 'the office'}` : 'Set by them'}
              {p.note ? `: ${p.note}` : ''}
            </span>
          ) : null}
        </p>
      ) : (
        <p className="text-muted">Loading…</p>
      )}
      {p && mayManage ? (
        <>
          <Field
            label={`How did ${member.displayName} agree?`}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="quiet"
              disabled={save.isPending || note.trim() === ''}
              onClick={() => save.mutate(!p.shared)}
            >
              {p.shared ? 'Hide' : 'Show'}
            </Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <button type="button" className="link self-start" onClick={() => setOpen(false)}>
          Close
        </button>
      )}
      {sharing.isError || save.isError ? (
        <p role="alert" className="alert">
          {(sharing.error ?? save.error)?.message}
        </p>
      ) : null}
    </div>
  );
}

export function CrewForm({ member, onDone }: { member?: CrewMember; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(member?.displayName ?? '');
  const [phone, setPhone] = useState(member?.phone ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [crewClass, setCrewClass] = useState<CrewClass>(member?.class ?? 'contractor');
  const [companyName, setCompanyName] = useState(member?.companyName ?? '');
  const [notes, setNotes] = useState(member?.notes ?? '');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: CrewMemberInput) =>
      member ? updateCrewMember(member.id, input) : createCrewMember(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crew'] });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) return setProblem('Give their name.');
    if (crewClass === 'subcontractor' && !companyName.trim()) {
      return setProblem('A subcontractor comes from another company: say which.');
    }
    setProblem(undefined);
    save.mutate({
      displayName: displayName.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      class: crewClass,
      companyName: companyName.trim() || null,
      notes: notes.trim() || null,
    });
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4" aria-label="Crew member">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Name"
          value={displayName}
          autoComplete="off"
          onChange={(e) => setDisplayName(e.currentTarget.value)}
        />
        <Field
          label="Phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs text-ink-soft">Class</legend>
        <div className="flex flex-wrap gap-4">
          {(Object.keys(CREW_CLASS) as CrewClass[]).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="crew-class"
                value={k}
                checked={crewClass === k}
                onChange={() => setCrewClass(k)}
              />
              {CREW_CLASS[k]}
            </label>
          ))}
        </div>
      </fieldset>
      {crewClass === 'subcontractor' || companyName ? (
        <Field
          label="Company"
          hint="The AV company they come from."
          value={companyName}
          onChange={(e) => setCompanyName(e.currentTarget.value)}
        />
      ) : null}
      <TextArea
        label="Notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {member ? 'Save' : 'Add'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

type PairKey = 'startEarlier' | 'startLater' | 'endLater' | 'endEarlier' | 'call';
type FieldKey =
  | 'moved'
  | 'role'
  | 'rate'
  | 'meetingPoint'
  | 'direct'
  | 'dressCode'
  | 'crewNotes'
  | 'instructions'
  | 'contact'
  | 'taskAdded';

const PAIRS: ReadonlyArray<[PairKey, string]> = [
  ['startEarlier', 'Start moves earlier'],
  ['startLater', 'Start moves later'],
  ['endLater', 'End moves later'],
  ['endEarlier', 'End moves earlier'],
  ['call', 'Call time moves'],
];
const FIELDS: ReadonlyArray<[FieldKey, string]> = [
  ['moved', 'Where they report'],
  ['role', 'Role'],
  ['rate', 'Agreed rate'],
  ['meetingPoint', 'Meeting point'],
  ['direct', 'Straight to / home from site'],
  ['dressCode', 'Dress code'],
  ['crewNotes', 'Notes for the crew'],
  // VEN-09 at v1.974: the job's copies of its venue's standing instructions.
  ['instructions', 'Standing instructions'],
  // CRW-14 at v1.996.
  ['contact', 'Contact'],
  ['taskAdded', 'A task added once it’s started'],
];
const OUTCOME_LABEL = {
  none: 'Nothing',
  // CRW-14 at v1.975: their confirmation stands; they see what changed and tap Got it.
  notify: 'Tell them',
  reconfirm: 'Re-confirm',
  reaccept: 'Re-accept',
} as const;

/**
 * CRW-14 at v1.964: the company's rules for how much change to an accepted shift asks someone to
 * re-confirm it, or to accept it again. Measured against what they last said yes to, so small moves
 * add up. Minutes in, minutes out; an empty re-accept box is "never".
 */
export function ChangeRulesPanel({ mayManage }: { mayManage: boolean }) {
  const rules = useQuery(changeRulesQuery);
  const [editing, setEditing] = useState(false);
  return (
    <section className="flex flex-col gap-3" aria-labelledby="change-rules">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="change-rules" className="section-title flex-1">
          When a shift changes
        </h2>
        {mayManage && !editing && rules.data ? (
          <Button variant="quiet" onClick={() => setEditing(true)}>
            Edit rules
          </Button>
        ) : null}
      </div>
      <p className="text-sm text-ink-soft">
        After someone accepts a shift, a change is measured against what they last said yes to.
        Small changes ask nothing; bigger ones ask them to confirm again; the biggest send the shift
        back for them to accept again, and they stop being held for it.
      </p>
      {rules.isError ? (
        <p role="alert" className="alert">
          {rules.error.message}
        </p>
      ) : null}
      {rules.data && editing ? (
        <ChangeRulesForm rules={rules.data} onDone={() => setEditing(false)} />
      ) : null}
      {rules.data && !editing ? <ChangeRulesSummary rules={rules.data} /> : null}
    </section>
  );
}

const minutes = (m: number | null | undefined) =>
  m === null || m === undefined ? 'never' : m % 60 === 0 && m >= 60 ? `${m / 60} h` : `${m} min`;

/** CRW-16 at v1.978: what crew see of a job shift before the office confirms it. */
const PRE_CONFIRMATION_LABEL = {
  day: 'The day only',
  summary: 'Role, job, venue and planned times',
  full: 'Everything, marked provisional',
} as const;

function ChangeRulesSummary({ rules }: { rules: ChangeRules }) {
  return (
    <div className="flex flex-col gap-3">
      {/* CRW-16 at v1.978: the confirmation stage, before any of the change rules apply. */}
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        {/* CRW-11 at v1.980: when a held day should have its job. */}
        <dt className="text-ink-soft">Place held days by</dt>
        <dd>{rules.assignWindowDays} days before the day</dd>
        {/* OPS-02 at v1.996: when crew see their tasks. */}
        <dt className="text-ink-soft">Crew see tasks from</dt>
        <dd>{rules.taskVisibilityDays} days before</dd>
        <dt className="text-ink-soft">Confirm shifts by</dt>
        <dd>{rules.confirmWindowHours} hours before they start</dd>
        <dt className="text-ink-soft">Before confirmation crew see</dt>
        <dd>{PRE_CONFIRMATION_LABEL[rules.preConfirmationView]}</dd>
        {/* CRW-08 at v1.979: shown to crew on every confirmed job shift. */}
        <dt className="text-ink-soft">Office number for crew</dt>
        <dd>{rules.officePhone ?? 'None — crew see no office number'}</dd>
      </dl>
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">Times</caption>
          <thead>
            <tr>
              <th scope="col">Change</th>
              <th scope="col">Re-confirm beyond</th>
              <th scope="col">Re-accept beyond</th>
            </tr>
          </thead>
          <tbody>
            {PAIRS.map(([key, label]) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                <td>{minutes(rules[key].reconfirmAfter)}</td>
                <td>{minutes(rules[key].reacceptAfter)}</td>
              </tr>
            ))}
            {FIELDS.map(([key, label]) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                <td colSpan={2}>{OUTCOME_LABEL[rules[key]]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChangeRulesForm({ rules, onDone }: { rules: ChangeRules; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ChangeRules>(rules);
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: () => saveChangeRules(draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: changeRulesQuery.queryKey });
      onDone();
    },
  });
  const setPair = (key: PairKey, part: 'reconfirmAfter' | 'reacceptAfter', text: string) => {
    const value = text.trim() === '' ? null : Number(text);
    setDraft((d) => ({ ...d, [key]: { ...d[key], [part]: value } }));
  };

  function submit(e: FormEvent) {
    e.preventDefault();
    if (
      !Number.isInteger(draft.confirmWindowHours) ||
      draft.confirmWindowHours < 1 ||
      draft.confirmWindowHours > 720
    ) {
      return setProblem('Confirm shifts by: a whole number of hours, from 1 to 720.');
    }
    if (
      !Number.isInteger(draft.assignWindowDays) ||
      draft.assignWindowDays < 1 ||
      draft.assignWindowDays > 60
    ) {
      return setProblem('Place held days by: a whole number of days, from 1 to 60.');
    }
    if (
      !Number.isInteger(draft.taskVisibilityDays) ||
      draft.taskVisibilityDays < 1 ||
      draft.taskVisibilityDays > 60
    ) {
      return setProblem('Crew see tasks from: a whole number of days, from 1 to 60.');
    }
    for (const [key, label] of PAIRS) {
      const p = draft[key];
      if (
        p.reconfirmAfter === null ||
        !Number.isInteger(p.reconfirmAfter) ||
        p.reconfirmAfter < 0
      ) {
        return setProblem(`${label}: give a whole number of minutes to re-confirm beyond.`);
      }
      const reaccept = p.reacceptAfter ?? null;
      if (reaccept !== null && (!Number.isInteger(reaccept) || reaccept < p.reconfirmAfter)) {
        return setProblem(
          `${label}: re-accept is at least the re-confirm minutes, or left empty for never.`,
        );
      }
    }
    setProblem(undefined);
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4" aria-label="Change rules">
      {/* CRW-16 at v1.978: the confirmation stage. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Place held days by (days before the day)"
          inputMode="numeric"
          value={Number.isNaN(draft.assignWindowDays) ? '' : draft.assignWindowDays.toString()}
          onChange={(e) => {
            const value = Number(e.currentTarget.value);
            setDraft((d) => ({ ...d, assignWindowDays: value }));
          }}
        />
        <Field
          label="Crew see tasks from (days before)"
          inputMode="numeric"
          value={Number.isNaN(draft.taskVisibilityDays) ? '' : draft.taskVisibilityDays.toString()}
          onChange={(e) => {
            const value = Number(e.currentTarget.value);
            setDraft((d) => ({ ...d, taskVisibilityDays: value }));
          }}
        />
        <Field
          label="Confirm shifts by (hours before they start)"
          inputMode="numeric"
          value={Number.isNaN(draft.confirmWindowHours) ? '' : draft.confirmWindowHours.toString()}
          onChange={(e) => {
            const value = Number(e.currentTarget.value);
            setDraft((d) => ({ ...d, confirmWindowHours: value }));
          }}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pre-confirmation-view" className="text-xs text-ink-soft">
            Before confirmation crew see
          </label>
          <select
            id="pre-confirmation-view"
            className="input"
            value={draft.preConfirmationView}
            onChange={(e) => {
              const value = e.currentTarget.value as ChangeRules['preConfirmationView'];
              setDraft((d) => ({ ...d, preConfirmationView: value }));
            }}
          >
            {(Object.keys(PRE_CONFIRMATION_LABEL) as Array<ChangeRules['preConfirmationView']>).map(
              (v) => (
                <option key={v} value={v}>
                  {PRE_CONFIRMATION_LABEL[v]}
                </option>
              ),
            )}
          </select>
        </div>
        <Field
          label="Office number for crew (shown on every confirmed shift)"
          inputMode="tel"
          value={draft.officePhone ?? ''}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setDraft((d) => ({ ...d, officePhone: value }));
          }}
        />
      </div>
      <p className="text-sm text-ink-soft">
        Changes after confirmation, in minutes. Leave “re-accept” empty if that change should never
        send the shift back.
      </p>
      {PAIRS.map(([key, label]) => (
        <fieldset key={key} className="grid gap-3 sm:grid-cols-[1fr_9rem_9rem] sm:items-end">
          <legend className="sr-only">{label}</legend>
          <span className="text-sm font-semibold sm:pb-2">{label}</span>
          <Field
            label="Re-confirm beyond"
            inputMode="numeric"
            value={draft[key].reconfirmAfter?.toString() ?? ''}
            onChange={(e) => setPair(key, 'reconfirmAfter', e.currentTarget.value)}
          />
          <Field
            label="Re-accept beyond"
            inputMode="numeric"
            value={draft[key].reacceptAfter?.toString() ?? ''}
            onChange={(e) => setPair(key, 'reacceptAfter', e.currentTarget.value)}
          />
        </fieldset>
      ))}
      {FIELDS.map(([key, label]) => (
        <div key={key} className="grid gap-2 sm:grid-cols-[1fr_18rem] sm:items-center">
          <label htmlFor={`rule-${key}`} className="text-sm font-semibold">
            {label}
          </label>
          <select
            id={`rule-${key}`}
            className="input"
            value={draft[key]}
            onChange={(e) => {
              // Read now: React clears the event's target before a state updater runs.
              const value = e.currentTarget.value as ChangeRules[FieldKey];
              setDraft((d) => ({ ...d, [key]: value }));
            }}
          >
            <option value="none">Nothing</option>
            <option value="notify">Notify</option>
            <option value="reconfirm">Re-confirm</option>
            <option value="reaccept">Re-accept</option>
          </select>
        </div>
      ))}
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
